import * as React from 'react';
import {
  Grid,
  Box,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  styled,
  Button
} from '@material-ui/core';
import { isNumber, get } from 'lodash';
import { ItemValue, Model, QuestionRadiogroupModel } from 'survey-core';
import { Loading } from '@pec/aion-ui-components/components/Loading';
import { ComplianceLevels } from '../../types/enums';
import { SurveyQuestionElementBase } from 'survey-react-ui';
import CheckOutlinedIcon from '@material-ui/icons/CheckOutlined';
import CloseOutlinedIcon from '@material-ui/icons/CloseOutlined';
import StarIcon from '@material-ui/icons/Star';

export const TYPE = 'mui-subscriptions';
export const CLOSE = 'Close';
export const CHECK = 'Check';
export const SELECTED_PRODUCT_ICON = 'orderPageSelectedProductIconSrc';

const productSortingOrder = [
  ComplianceLevels.Standard,
  ComplianceLevels.Advanced,
  ComplianceLevels.VerifiedContractor,
  ComplianceLevels.VerifiedSupplier,
  ComplianceLevels.Elite
];

type ProductDetails = {
  description: {
    text: string;
    icon: string;
  }[];
  isMostPopular?: boolean;
};

export const TestLocators = {
  price: 'price'
};

export class MuiSubscriptionsModel extends QuestionRadiogroupModel {
  getType(): string {
    return TYPE;
  }

  get orderByOriginalItem() {
    return this.getPropertyValue('orderByOriginalItem');
  }
  set orderByOriginalItem(value) {
    this.setPropertyValue('orderByOriginalItem', value);
  }

  get filterChoicesOnRender() {
    return this.getPropertyValue('filterChoicesOnRender');
  }
  set filterChoicesOnRender(value) {
    this.setPropertyValue('filterChoicesOnRender', value);
  }

  get price() {
    return this.getPropertyValue('price');
  }
  set price(value) {
    this.setPropertyValue('price', value);
  }

  get productDetails() {
    return this.getPropertyValue('productDetails');
  }
  set productDetails(value) {
    this.setPropertyValue('productDetails', value);
  }

  get productIcons() {
    return this.getPropertyValue('productIcons');
  }
  set productIcons(value) {
    this.setPropertyValue('productIcons', value);
  }

  get strings() {
    return this.getPropertyValue('strings');
  }
  set strings(val) {
    this.setPropertyValue('strings', val);
  }
}

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'var(--gray)',
  border: '1px solid var(--gray-border)',
  borderRadius: '12px',
  '&:hover': {
    backgroundColor: 'var(--primary-04-)',
    cursor: 'pointer'
  },
  '&.selected': {
    backgroundColor: 'var(--primary-04-)',
    border: '1px solid var(--primary)'
  },
  '& .MuiCardHeader-root': {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    paddingBottom: theme.spacing(1),
    [theme.breakpoints.down('md')]: {
      paddingTop: theme.spacing(3)
    },
    '& .MuiTypography-root .MuiTypography-body2': {
      fontSize: '0.75rem',
      lineHeight: '1rem',
      letterSpacing: '0.025em',
      color: 'var(--black)'
    },
    '& .MuiTypography-root.MuiTypography-h5': {
      color: 'var(--black)',
      '&:first-child': {
        [theme.breakpoints.down('md')]: {
          marginTop: theme.spacing(-0.75)
        }
      },
      '&:last-child': {
        [theme.breakpoints.down('md')]: {
          fontSize: 28
        },
        [theme.breakpoints.down('xs')]: {
          fontSize: 22
        }
      }
    }
  },
  '& .MuiCardContent-root': {
    padding: theme.spacing(1, 3),
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(0.25, 2.5)
    }
  },
  '& .MuiListItem-root': {
    padding: 0,
    alignItems: 'start',
    '& .MuiListItemIcon-root': {
      minWidth: 30,
      [theme.breakpoints.down('md')]: {
        marginTop: 2,
        minWidth: '20px !important'
      },
      '& svg': {
        [theme.breakpoints.down('md')]: {
          fontSize: '1rem'
        }
      }
    },
    '& .MuiListItemText-root': {
      margin: '0px',
      [theme.breakpoints.down('md')]: {
        lineHeight: '16px'
      },
      '&.excluded-option .MuiTypography-caption': {
        color: 'var(--light-gray)'
      }
    }
  },
  '& .MuiCardActions-root': {
    marginTop: 'auto',
    justifyContent: 'center',
    padding: theme.spacing(0.5, 3, 2),
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(1.5, 3, 2.75)
    }
  },
  '& .MuiCardActions-root .MuiButton-root': {
    border: `1px solid var(--light-outline)`,
    borderRadius: '100px',
    textTransform: 'unset',
    '&:disabled': {
      backgroundColor: 'var(--disabled)',
      borderColor: 'var(--disabled)'
    }
  }
}));

const StyledButton = styled(Button)(() => ({
  '&.MuiButton-root[aria-disabled="true"]': {
    borderColor: 'var(--disabled)',
    backgroundColor: 'var(--disabled)',
    color: 'var(--disabled-button)'
  }
}));

export class MuiSubscriptions extends SurveyQuestionElementBase {
  buttonRef = React.createRef<any>();

  constructor(props: any) {
    super(props);
    this.handleChange = this.handleChange.bind(this);
  }

  componentDidMount() {
    super.componentDidMount();
    this.question.questionRef = this.buttonRef;
  }

  get question() {
    return this.questionBase;
  }

  get choices() {
    return this.question.visibleChoices;
  }

  get orderByOriginalItem() {
    return this.question.orderByOriginalItem;
  }

  get filterChoicesOnRender() {
    return this.question.filterChoicesOnRender;
  }

  get price() {
    return this.question.price;
  }

  get productDetails() {
    return this.question.productDetails;
  }

  get productIcons() {
    return this.question.productIcons;
  }

  getString(key: string) {
    return get(this.question, ['strings', key], key);
  }

  handleClick(value: string, text: string) {
    this.question.value = value;
    sessionStorage.setItem(SELECTED_PRODUCT_ICON, this.getProductIcon(text));
  }

  handleChange({ target: { value } }: React.ChangeEvent<HTMLInputElement>) {
    this.question.value = value;
  }

  getChoicesForRender(choices: ItemValue[]) {
    const { survey } = this.question;
    const filteredChoices = choices.filter((item: ItemValue) => {
      if (this.filterChoicesOnRender) {
        const condition = this.filterChoicesOnRender.replace(/\{item}/g, `'${item.value}'`);
        return (survey as Model).runCondition(condition);
      }
      return true;
    });

    this.updateFilteredProducts(filteredChoices);

    const sortedChoices = filteredChoices
      .filter(choice => productSortingOrder.includes(choice.originalItem.subscriptionTier))
      .sort(function(a, b) {
        return (
          productSortingOrder.indexOf(a.originalItem.subscriptionTier) -
          productSortingOrder.indexOf(b.originalItem.subscriptionTier)
        );
      });

    const distinctChoices = filteredChoices.filter(o => sortedChoices.indexOf(o) === -1);

    distinctChoices.sort((a: ItemValue, b: ItemValue) => {
      if (this.orderByOriginalItem) {
        const [field, order] = this.orderByOriginalItem.split(':');
        if (isNumber(a.originalItem[field])) {
          return order === 'desc'
            ? b.originalItem[field] - a.originalItem[field]
            : a.originalItem[field] - b.originalItem[field];
        }
        return order === 'desc'
          ? b.originalItem[field].localeCompare(a.originalItem[field])
          : a.originalItem[field].localeCompare(b.originalItem[field]);
      }
      return 0;
    });

    return sortedChoices.concat(distinctChoices);
  }

  getPrice(value: string) {
    const { survey } = this.question;
    const joiningFeeProductId = (survey as Model).runExpression("getJoiningFee('id')");

    if (this.price) {
      const expression = this.question.price
        .replace(/\{value}/g, `'${value}'`)
        .replace(/\{joiningFeeProductId}/g, `'${joiningFeeProductId}'`);

      return survey.runExpression(expression);
    }
    return null;
  }

  getProductDetails(value: string): ProductDetails | undefined {
    const { survey } = this.question;
    let result;

    if (this.question.productDetails) {
      this.question.productDetails.forEach((element: any) => {
        if (survey.runExpression(element.visibleIf.replace(/\{value}/g, `'${value}'`))) {
          result = element as ProductDetails;
        }
      });
    }
    return result;
  }

  getProductIcon(value: string): string {
    const { survey } = this.question;
    let expression: string = '';
    let result: string = '';

    if (this.question.productIcons) {
      this.question.productIcons.forEach((element: any) => {
        expression = element.visibleIf.replace(/\{subscriptionTier}/g, `'${value}'`);
        if (survey.runExpression(expression)) {
          result = element.iconSrc;
        }
      });
      return result;
    }
    return result;
  }

  getStyledProductExplanation(description: { text: string; icon: string }) {
    switch (description.icon) {
      case CHECK:
        return (
          <>
            <ListItemIcon>
              <CheckOutlinedIcon htmlColor="var(--green)" fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              <Typography variant="caption">{description.text}</Typography>
            </ListItemText>
          </>
        );
      case CLOSE:
        return (
          <>
            <ListItemIcon>
              <CloseOutlinedIcon htmlColor="var(--error)" fontSize="small" />
            </ListItemIcon>
            <ListItemText className="excluded-option">
              <Typography variant="caption">{description.text}</Typography>
            </ListItemText>
          </>
        );
      default:
        return <ListItemText>{description.text}</ListItemText>;
    }
  }

  // MuiDiscount uses filtered choices values list to verify code
  updateFilteredProducts(filteredChoices: ItemValue[]) {
    const { survey } = this.question;

    const filteredProducts = (survey as Model).getVariable('filteredProducts') || {};
    (survey as Model).setVariable('filteredProducts', {
      ...filteredProducts,
      [this.question.name]: filteredChoices.map((item: ItemValue) => item.value)
    });
  }

  protected renderElement(): JSX.Element | null {
    const { survey } = this.question;
    const { packageType, organizationType } = (survey as Model).data;
    const selectedValue = this.question.value ?? '';
    const taxRate = (survey as Model).getVariable('taxRate');
    const joiningFeeProductId = (survey as Model).runExpression("getJoiningFee('id')");

    if (this.choices.length === 0) {
      return <Loading />;
    }

    const choices = this.getChoicesForRender(this.choices);

    return (
      <Grid style={{ marginTop: '32px', marginBottom: '4px', justifyContent: 'center' }} container spacing={3}>
        {choices.map(({ value, text }: ItemValue, index: number) => (
          <Grid
            item
            xs={12}
            lg={choices.length === 2 ? 5 : 4}
            key={value}
            data-packageType={packageType}
            data-organizationType={organizationType}
            data-itemsCount={choices.length}
          >
            {this.getProductDetails(text)?.isMostPopular && (
              <Box display="flex" justifyContent="center" alignItems="center" height={32} mt={-4}>
                <StarIcon htmlColor="var(--yellow)" />
                <Typography style={{ fontStyle: 'italic' }} variant="caption">
                  {this.getString('mostPopular')}
                </Typography>
              </Box>
            )}
            <StyledCard
              className={`${value === selectedValue ? 'selected' : ''}`}
              onClick={() => {
                this.handleClick(value, text);
              }}
            >
              <CardHeader
                avatar={<img alt={text} src={`${process.env.PUBLIC_URL}${this.getProductIcon(text)}`} />}
                title={<Typography variant="h5">{text.replace('CHAS', '')}</Typography>}
                subheader={
                  <Typography variant="h5" data-testid={TestLocators.price}>
                    {this.getPrice(value)}
                    <Typography component={'span'} variant="body2">
                      {taxRate?.isActive || joiningFeeProductId ? this.getString('year') : this.getString('yearNoTax')}
                    </Typography>
                  </Typography>
                }
              />
              <CardContent>
                <List disablePadding>
                  {this.getProductDetails(text)?.description.map((description, index) => (
                    <ListItem key={index}>{this.getStyledProductExplanation(description)}</ListItem>
                  ))}
                </List>
              </CardContent>
              <CardActions>
                <StyledButton
                  ref={index === 0 ? this.buttonRef : null}
                  aria-disabled={value === selectedValue}
                  tabIndex={1}
                  variant="outlined"
                  fullWidth
                  color="primary"
                  onClick={event => {
                    this.handleClick(value, text);
                    event.stopPropagation();
                  }}
                >
                  {value === selectedValue ? this.getString('selected') : this.getString('select')}
                </StyledButton>
              </CardActions>
            </StyledCard>
          </Grid>
        ))}
      </Grid>
    );
  }
}
